import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { formatShortDate } from "../chiefComplaints/dateHelpers";
import MedicineService from "./MedicineService";
import MetaTooltip from "./MetaTooltip";
import { medicineBand } from "./data";
import ModalPortal from "../common/ModalPortal";

/**
 * "View trend" modal for a Medicine.
 *
 * Audit columns (8): Timestamp · Since when · To when · Description ·
 * Reported by · Frequency · Instructions · Status. Dose column deliberately
 * dropped — the description carries any dose narrative that matters, and
 * omitting it reclaims horizontal room for the timestamp + reported-by cells.
 *
 * Each StatusEntry may carry 0..N AdherenceReviews. They render as
 * distinct full-width rows nested under the parent, spanning all 8 grid
 * columns — a different layout, deliberately, so the compliance story is
 * scannable at a glance.
 */

// Merged Frequency (Dose qty.) label — mirrors the table's
// frequencyDoseText: cadence + qty in one column, dose trailing in
// parens. `asNeeded` rewrites to "As needed" so the audit reads
// naturally instead of surfacing the raw enum key.
const slotLabel = (s) => (s === "asNeeded" ? "As needed" : s);
const fmtFreqDose = (row) => {
  if (!row) return "—";
  const f = row.doseFrequency || {};
  const dq = row.doseQuantity || {};
  const per = row.perSlotQuantities || {};
  const hasPerSlot = per && Object.keys(per).length > 0;
  const asNeededSlot =
    Array.isArray(f.timesOfDay) && f.timesOfDay.includes("asNeeded");

  const parens = (v, u) => {
    const t = `${v || ""} ${u || ""}`.trim();
    return t ? ` (${t})` : "";
  };

  if (hasPerSlot) {
    const order = ["Morning", "Afternoon", "Evening", "Night"];
    const parts = [];
    order.forEach((slot) => {
      const q = per[slot];
      if (!q) return;
      parts.push(`${slot}${parens(q.value, q.unit)}`);
    });
    if (parts.length > 0) return parts.join(", ");
  }
  if (asNeededSlot) return `As needed${parens(dq.value, dq.unit)}`;
  if (Array.isArray(f.timesOfDay) && f.timesOfDay.length > 0) {
    return `${f.timesOfDay.map(slotLabel).join(", ")}${parens(dq.value, dq.unit)}`;
  }
  if (f.customValue && f.customUnit) {
    const abbrev = { Hours: "hrs", Days: "days", Weeks: "wks", Months: "mos" }[f.customUnit] || f.customUnit;
    return `Every ${f.customValue} ${abbrev}${parens(dq.value, dq.unit)}`;
  }
  const scalar = parens(dq.value, dq.unit).trim();
  return scalar || "—";
};

// Map the instruction key ("before") back to its short "before meal" label.
const INSTR_LABELS = {
  before: "before meal",
  with: "with meal",
  after: "after meal",
  "after-dinner": "after dinner",
  empty: "empty stomach",
};

// "27 Jul 2026" → same helper as other v2 modals so the mock's exact format
// falls out for free.
const OUTCOME_LABEL = {
  completed: "Completed",
  partially: "Partially completed",
  stopped_early: "Stopped early",
};
const OUTCOME_CLASS = {
  completed: "med-adh-pill-completed",
  partially: "med-adh-pill-partial",
  stopped_early: "med-adh-pill-stopped",
};

// "27 Jul 2026, 01:05 pm" — mirrors the mock string.
const dateAndTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  return `${date}, ${h}:${String(m).padStart(2, "0")} ${ampm}`;
};
const timeOnly = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")} ${ampm}`;
};

// Reporter fallback — server enriches ReportedByName / ByName at read
// time; when empty (upstream down / unknown role) fall back to the
// deterministic "Employee #21" label so the cell always shows something.
const reporterLabel = (actor, name) => {
  if (name) return name;
  if (!actor) return "—";
  const who = actor.who ? actor.who.charAt(0).toUpperCase() + actor.who.slice(1) : "";
  return actor.whoId ? `${who || "User"} #${actor.whoId}` : who || "—";
};

// Reporter label for a StatusEntry row. Cron-driven auto-completions
// (event="auto-status") always attribute to "System" regardless of who
// the actor was on the wire — the backend still stamps
// {who:"service", whoId:0} for audit, but the user-facing text should
// read "System" so it's obvious the row wasn't a manual doctor action.
const entryReporterLabel = (row) => {
  if (row && row.event === "auto-status") return "System";
  return reporterLabel(row && row.reportedBy, row && row.reportedByName);
};

const TrendModal = ({ patientId, medicine, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const svc = new MedicineService();
    svc.trend(
      patientId,
      medicine.medicineId,
      (res) => setData(res),
      (err) => setError((err && err.message) || "Failed to load trend")
    );
  }, [patientId, medicine.medicineId]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const currentBand = data ? medicineBand(data.status || "ongoing") : null;

  return (
    <ModalPortal>
      <div className="cc-modal-backdrop" onMouseDown={onClose}>
        <div
          className="cc-modal cc-modal-trend med-modal-trend"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <header className="cc-modal-head">
            <div>
              <span className="cc-modal-title">
                {medicine.medicine}
                {(medicine.formulationName || medicine.formulation) && (
                  <span className="med-form">
                    {" "}({medicine.formulationName || medicine.formulation})
                  </span>
                )}
              </span>
              {currentBand && (
                <span
                  className="cc-modal-tag"
                  style={{ color: currentBand.fg, background: currentBand.bg }}
                >
                  {currentBand.label}
                </span>
              )}
            </div>
            <button type="button" className="cc-modal-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </header>
  
          {!data && !error && <div className="cc-modal-body cc-modal-loading">Loading…</div>}
          {error && <div className="cc-modal-body cc-modal-error">{error}</div>}
  
          {data && (
            <div className="cc-modal-body">
              <div className="cc-trend-audit">
                <div className="cc-trend-audit-head">
                  <span>Audit</span>
                  <span className="cc-trend-audit-count">
                    · {data.audit ? data.audit.length : 0} entries
                  </span>
                </div>
                <div className="sec-table med-trend-grid" role="table">
                  <div className="sec-thead" role="row">
                    <span role="columnheader">Timestamp</span>
                    <span role="columnheader">Since when</span>
                    <span role="columnheader">To when</span>
                    <span role="columnheader">Description</span>
                    <span role="columnheader">Reported by</span>
                    <span role="columnheader">Frequency (Dose qty.)</span>
                    <span role="columnheader">Instructions</span>
                    <span role="columnheader">Status</span>
                  </div>
                  {/* Newest → oldest so the current regimen sits at the
                      top; the API doesn't index anything against this
                      order, so a plain reverse is safe. */}
                  {[...(data.audit || [])].reverse().map((row, idx) => {
                    const isDelete = row.event === "delete";
                    const isAuto = row.event === "auto-status";
                    const band = medicineBand(row.status || "ongoing");
                    const reviews = row.adherenceReviews || [];
                    return (
                      <React.Fragment key={idx}>
                        <div className="sec-row" role="row">
                          <span className="sec-cell med-trend-ts" role="cell">
                            <span className="med-trend-ts-date">
                              {formatShortDate(row.recordedAt)}
                            </span>
                            <span className="med-trend-ts-time">{timeOnly(row.recordedAt)}</span>
                          </span>
                          <span className="sec-cell" role="cell">
                            {formatShortDate(row.sinceWhen)}
                          </span>
                          <span className="sec-cell" role="cell">
                            {row.toWhen ? formatShortDate(row.toWhen) : "—"}
                          </span>
                          {/* Description clamps to two lines via the shared
                              MetaTooltip anchor / clamp classes; hovering
                              reveals the full text in a light-card popover
                              matching the Medicines table + Hospitalisation
                              Procedures tooltip. */}
                          <span className="sec-cell med3-trend-c-desc" role="cell">
                            <MetaTooltip text={row.description} />
                          </span>
                          <span className="sec-cell" role="cell">
                            {entryReporterLabel(row)}
                          </span>
                          <span className="sec-cell" role="cell">
                            {fmtFreqDose(row)}
                          </span>
                          <span className="sec-cell" role="cell">
                            {INSTR_LABELS[row.instructions] || row.instructions || "—"}
                          </span>
                          <span className="sec-cell" role="cell">
                            {isDelete ? (
                              <span className="cc-audit-deleted">Deleted</span>
                            ) : (
                              <span
                                className="cc-trend-band"
                                style={{ background: band.bg, color: band.fg }}
                                title={isAuto ? "Auto-completed by system on expiry" : undefined}
                              >
                                {isAuto ? "Auto-completed" : band.label}
                              </span>
                            )}
                          </span>
                        </div>
  
                        {reviews.map((r, ri) => (
                          <div className="med-trend-adh" role="row" key={`${idx}-adh-${ri}`}>
                            <div className="med-trend-adh-line1">
                              <span className="med-trend-adh-eyebrow">ADHERENCE</span>
                              <span className="med-trend-adh-when">· {dateAndTime(r.at)}</span>
                              <span className={`med-trend-adh-pill ${OUTCOME_CLASS[r.outcome] || ""}`}>
                                {OUTCOME_LABEL[r.outcome] || r.outcome}
                              </span>
                              <span className="med-trend-adh-window">
                                Start date: {formatShortDate(r.adherenceStart)}
                                <span className="med-trend-adh-window-sep">·</span>
                                End date: {formatShortDate(r.adherenceEnd)}
                              </span>
                            </div>
                            <div className="med-trend-adh-line2">
                              <span className="med-trend-adh-reporter">
                                {reporterLabel(r.by, r.byName)}
                              </span>
                              <span className="med-trend-adh-desc">
                                {r.description || "—"}
                              </span>
                            </div>
                          </div>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

TrendModal.propTypes = {
  patientId: PropTypes.number.isRequired,
  medicine: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TrendModal;
